import React, { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import s from './NovaTooltip.module.sass';

/**
 * Глобальный Nova-тултип. Перехватывает нативный `title` у любого элемента
 * внутри [data-nova-theme], прячет браузерную подсказку и рисует свою —
 * в стиле темы. Ничего не нужно менять в местах использования: достаточно
 * обычного title="…". Рендерится порталом в корень Nova, чтобы работали токены.
 */
export default function NovaTooltip() {
    const [tip, setTip] = useState(null); // { text, x, top, bottom, above }
    const timer = useRef(null);
    const currentEl = useRef(null);

    useEffect(() => {
        // Переносим title → data-nova-tip, чтобы браузер не показывал свой тултип
        const stash = (el) => {
            const t = el.getAttribute('title');
            if (t != null && t !== '') {
                el.setAttribute('data-nova-tip', t);
                el.removeAttribute('title');
            }
            return el.getAttribute('data-nova-tip');
        };

        const show = (el) => {
            const text = stash(el);
            if (!text) return;
            currentEl.current = el;
            clearTimeout(timer.current);
            timer.current = setTimeout(() => {
                if (currentEl.current !== el || !el.isConnected) return;
                const r = el.getBoundingClientRect();
                const above = r.top > 56;
                setTip({
                    text,
                    x: Math.round(r.left + r.width / 2),
                    top: Math.round(r.top),
                    bottom: Math.round(r.bottom),
                    above,
                });
            }, 340);
        };

        const onOver = (e) => {
            const el = e.target.closest && e.target.closest('[title], [data-nova-tip]');
            if (!el || !el.closest('[data-nova-theme]')) return;
            if (el === currentEl.current) return;
            show(el);
        };
        const onOut = (e) => {
            const el = e.target.closest && e.target.closest('[data-nova-tip]');
            if (el && el === currentEl.current) {
                clearTimeout(timer.current);
                currentEl.current = null;
                setTip(null);
            }
        };
        const hide = () => {
            clearTimeout(timer.current);
            currentEl.current = null;
            setTip(null);
        };

        document.addEventListener('mouseover', onOver, true);
        document.addEventListener('mouseout', onOut, true);
        document.addEventListener('mousedown', hide, true);
        window.addEventListener('scroll', hide, true);
        return () => {
            document.removeEventListener('mouseover', onOver, true);
            document.removeEventListener('mouseout', onOut, true);
            document.removeEventListener('mousedown', hide, true);
            window.removeEventListener('scroll', hide, true);
            clearTimeout(timer.current);
        };
    }, []);

    if (!tip) return null;
    const root = document.querySelector('[data-nova-theme]') || document.body;
    return createPortal(
        <div
            className={`${s.tip} ${tip.above ? s.above : s.below}`}
            style={{
                left: tip.x,
                top: tip.above ? tip.top - 9 : tip.bottom + 9,
            }}
            role="tooltip"
            data-capture-exclude="true"
        >
            {tip.text}
            <span className={s.arrow} />
        </div>,
        root
    );
}
